// Detects symptom scores that an earlier version of normalizeSymptomScores inflated
// (0–10 entries treated as the old 1–5 scale: 1→0, 2→4, 3→6) and works out the
// values the user actually entered, using the check-in's own tags ("Bloating (3/10)").
//
// Everything here is read-only. applyScoreRepair is a pure function that is not
// wired to any UI until the user approves the repair.

import { Symptom } from './types';
import { isTenScaleSymptom } from './storage';
import { DEFAULT_TRACKABLE_SYMPTOMS, TrackableSymptom } from './symptomCategories';

export interface ScoreChange {
  symptomId: string;
  label: string;
  stored: number;
  original: number;
}

export type CloudState =
  | 'matches-original' // cloud copy still holds what was entered
  | 'inflated' // cloud copy has the inflated value too
  | 'missing' // not in the cloud
  | 'unknown'; // cloud not checked / unreachable

export interface SymptomRepair {
  id: string;
  date: string;
  timestamp_iso: string;
  changes: ScoreChange[];
  cloud: CloudState;
}

const TAG_PATTERN = /([^,]+?)\s*\((\d+(?:\.\d+)?)\/10\)/g;

/** Parse "Bloating (3/10), Stomach Pain (2/10)" into { bloating: 3, stomach_pain: 2 }. */
export function parseTenScaleTags(tags: string, definitions: TrackableSymptom[]): Record<string, number> {
  const byName = new Map<string, string>();
  for (const d of [...DEFAULT_TRACKABLE_SYMPTOMS, ...definitions]) {
    byName.set(d.name.trim().toLowerCase(), d.id);
    if (d.shortName) byName.set(d.shortName.trim().toLowerCase(), d.id);
  }
  const out: Record<string, number> = {};
  for (const match of tags.matchAll(TAG_PATTERN)) {
    const id = byName.get(match[1].trim().toLowerCase());
    if (id) out[id] = Number(match[2]);
  }
  return out;
}

function storedScore(s: Symptom, id: string): number | undefined {
  const fromScores = s.symptom_scores?.[id];
  if (fromScores !== undefined) return fromScores;
  if (id === 'bloating') return s.bloating_score;
  if (id === 'stomach_pain') return s.soreness_score;
  if (id === 'mouth_taste') return s.bad_taste_score ?? undefined;
  return undefined;
}

/** Score changes needed to restore what was entered. Only symptoms named in the tags are considered. */
export function diffAgainstTags(s: Symptom, definitions: TrackableSymptom[]): ScoreChange[] {
  if (!isTenScaleSymptom(s)) return [];
  const entered = parseTenScaleTags(s.symptom_tags || '', definitions);
  const labels = new Map([...DEFAULT_TRACKABLE_SYMPTOMS, ...definitions].map((d) => [d.id, d.name]));
  const changes: ScoreChange[] = [];
  for (const [id, original] of Object.entries(entered)) {
    const stored = storedScore(s, id);
    if (stored !== undefined && stored !== original) {
      changes.push({ symptomId: id, label: labels.get(id) || id, stored, original });
    }
  }
  return changes;
}

export function findScoreRepairs(
  localSymptoms: Symptom[],
  definitions: TrackableSymptom[],
  cloudSymptoms?: Symptom[] | null
): SymptomRepair[] {
  const cloudById = cloudSymptoms ? new Map(cloudSymptoms.map((c) => [c.id, c])) : null;
  const repairs: SymptomRepair[] = [];

  for (const s of localSymptoms) {
    const changes = diffAgainstTags(s, definitions);
    if (changes.length === 0) continue;

    let cloud: CloudState = 'unknown';
    if (cloudById) {
      const c = cloudById.get(s.id);
      if (!c) cloud = 'missing';
      else cloud = changes.every((ch) => storedScore(c, ch.symptomId) === ch.original) ? 'matches-original' : 'inflated';
    }

    repairs.push({ id: s.id, date: s.date, timestamp_iso: s.timestamp_iso, changes, cloud });
  }

  return repairs.sort((a, b) => b.timestamp_iso.localeCompare(a.timestamp_iso));
}

/** Pure: returns the corrected record. Not called anywhere until the repair is approved. */
export function applyScoreRepair(s: Symptom, repair: SymptomRepair): Symptom {
  const scores = { ...(s.symptom_scores || {}) };
  for (const ch of repair.changes) scores[ch.symptomId] = ch.original;
  return {
    ...s,
    symptom_scores: scores,
    bloating_score: scores.bloating ?? s.bloating_score,
    soreness_score: scores.stomach_pain ?? s.soreness_score,
    bad_taste_score: scores.mouth_taste ?? s.bad_taste_score,
  };
}
